import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

type AllowItem = {
  id: number;
  email: string;
  is_superadmin: boolean;
  is_active: boolean;
  notes?: string;
  created_at?: string;
};

export default function AdminAccessControl() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<AllowItem[]>({ queryKey: ['/api/admin/allowlist'] });
  const [email, setEmail] = useState('');
  const [isSuper, setIsSuper] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['/api/admin/allowlist'] });

  const add = async () => {
    if (!email.trim()) return;
    await apiRequest('/api/admin/allowlist', { method: 'POST', body: { email: email.trim(), is_superadmin: isSuper, is_active: true } });
    setEmail(''); setIsSuper(false); refresh();
  };

  const update = async (row: AllowItem, patch: Partial<AllowItem>) => {
    await apiRequest(`/api/admin/allowlist/${row.id}`, { method: 'PATCH', body: patch });
    refresh();
  };

  const remove = async (row: AllowItem) => {
    await apiRequest(`/api/admin/allowlist/${row.id}`, { method: 'DELETE' });
    refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-white">Access Control</h2>
        <p className="text-sm text-gray-400">Manage who can sign in and who is a SuperAdmin. Changes apply within ~60 seconds.</p>
      </div>

      <div className="flex gap-2 items-center">
        <Input placeholder="user@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="max-w-sm" />
        <div className="flex items-center gap-2">
          <Switch checked={isSuper} onCheckedChange={setIsSuper} />
          <span className="text-gray-300 text-sm">SuperAdmin</span>
        </div>
        <Button onClick={add}>Add</Button>
      </div>

      <div className="overflow-auto rounded border border-gray-700">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-800 text-gray-300">
            <tr>
              <th className="px-3 py-2 text-left">Email</th>
              <th className="px-3 py-2">Active</th>
              <th className="px-3 py-2">SuperAdmin</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="px-3 py-4 text-gray-400" colSpan={4}>Loading…</td></tr>
            ) : (data || []).map(row => (
              <tr key={row.id} className="border-t border-gray-800">
                <td className="px-3 py-2 text-gray-100">{row.email}</td>
                <td className="px-3 py-2 text-center">
                  <Switch checked={row.is_active} onCheckedChange={(v) => update(row, { is_active: v })} />
                </td>
                <td className="px-3 py-2 text-center">
                  <Switch checked={row.is_superadmin} onCheckedChange={(v) => update(row, { is_superadmin: v })} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="outline" onClick={() => remove(row)}>Remove</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

